import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from '@obraplan/core/domain/planning/errors';
import type { OutboxEntry } from '@/app/outbox-types';
import { comAFila } from './fila-da-conferencia';

/**
 * A frente × serviço que a pessoa já tem no dia, com a fila do aparelho por cima (Fase 43,
 * RN-643).
 *
 * O servidor não lança de novo a mesma frente × serviço para a mesma pessoa no mesmo dia
 * (`REPORTED_WORK_ALREADY_PLANNED`). Com sinal, a recusa vem na hora. Sem sinal, o lançamento
 * repetido ia para a fila e só era recusado na volta, quando a pessoa já tinha saído do
 * pavimento. O formulário barra antes, com a mesma mensagem — e, na equipe, com o nome de quem
 * já tem a frente, como o servidor diz.
 */

/** A mensagem do servidor, a mesma da recusa na volta. */
export const FRENTE_REPETIDA =
  PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORTED_WORK_ALREADY_PLANNED] ??
  'Esta pessoa já tem esta frente neste dia.';

/** A chave natural da alocação no dia, sem o plano: pessoa, frente e serviço. */
export function chaveDaFrente(
  resourceId: string,
  activityId: string,
  serviceId: string | undefined,
): string {
  return `${resourceId}|${activityId}|${serviceId ?? ''}`;
}

/**
 * As frentes × serviço de cada pessoa no dia: as do servidor (com o ajuste e o "Tirar" da fila,
 * como o cartão mostra) e as que os lançamentos da fila vão criar.
 *
 * **Só o que espera conta.** O lançamento recusado fica no aparelho para revisar; quem o refaz
 * corrigido não pode ser barrado por ele. Se o recusado for reenviado depois, o servidor o recusa
 * de novo, e o aviso dele diz por quê.
 */
export function frentesDoDia(
  entries: readonly OutboxEntry[],
  linhas: readonly InspectionLine[] = [],
): ReadonlySet<string> {
  const esperando = entries.filter((entry) => entry.state === 'pending');
  const frentes = new Set<string>();

  for (const linha of linhas) {
    const daPessoa = esperando.filter(
      (entry) => entry.operation.resourceId === linha.resource.resourceId,
    );
    for (const { allocation } of comAFila(linha, daPessoa).allocations) {
      frentes.add(
        chaveDaFrente(linha.resource.resourceId, allocation.activityId, allocation.serviceId),
      );
    }
  }

  for (const { operation } of esperando) {
    if (operation.kind === 'lancar-trabalho') {
      const { resourceId, activityId, serviceId } = operation.input;
      frentes.add(chaveDaFrente(resourceId, activityId, serviceId));
    } else if (operation.kind === 'lancar-trabalho-equipe') {
      const { activityId, serviceId } = operation.input;
      for (const membro of operation.input.members) {
        frentes.add(chaveDaFrente(membro.resourceId, activityId, serviceId));
      }
    }
  }
  return frentes;
}
